import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Stack, IconButton, Avatar, Button, Skeleton } from '@mui/material';
import {
  TrendingUp,
  People,
  Article,
  Message,
  ArrowForward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAllContent, getAdminEnquiries, getAllFAQs } from '../api/apiService';

const StatCard = ({ title, value, icon, color, gradient, onClick }) => (
  <Paper
    onClick={onClick}
    sx={{
      p: 3,
      borderRadius: '20px',
      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
      position: 'relative',
      overflow: 'hidden',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      border: '1px solid rgba(0, 0, 0, 0.03)',
      '&:hover': onClick ? { 
        transform: 'translateY(-6px)',
        boxShadow: '0px 12px 30px rgba(0, 0, 0, 0.1)',
        '& .arrow-icon': { transform: 'translateX(4px)' }
      } : {},
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: -10,
        right: -10,
        width: 80,
        height: 80,
        borderRadius: '50%',
        background: gradient || 'rgba(0,0,0,0.03)',
        opacity: 0.08,
      }}
    />
    <Stack direction="row" spacing={2} sx={{ mb: 3, alignItems: 'center' }}>
      <Box
        sx={{
          p: 1.5,
          borderRadius: '12px',
          bgcolor: `${color}15`,
          color: color,
          display: 'flex',
          boxShadow: `0px 4px 12px ${color}20`,
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', fontSize: '11px' }}>
        {title}
      </Typography>
    </Stack>
    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
      <Typography variant="h3" sx={{ fontWeight: 800, color: '#1A1A1A', letterSpacing: '-1px' }}>
        {value}
      </Typography>
      <IconButton 
        className="arrow-icon"
        size="small" 
        sx={{ 
          color: color, 
          bgcolor: `${color}10`,
          transition: 'transform 0.2s ease'
        }}
      >
        <ArrowForward fontSize="small" />
      </IconButton>
    </Stack>
  </Paper>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({
    blogs: 0,
    news: 0,
    enquiries: 0,
    faqs: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [blogsData, newsData, enquiriesData, faqsData, allRecent] = await Promise.all([
          getAllContent('Blog', 1, 1),
          getAllContent('News', 1, 1),
          getAdminEnquiries(1, 1),
          getAllFAQs(),
          getAllContent('', 1, 5)
        ]);

        setCounts({
          blogs: blogsData.total || 0,
          news: newsData.total || 0,
          enquiries: enquiriesData.total || 0,
          faqs: faqsData.data?.length || 0
        });

        setRecentActivities(allRecent.data || []);
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const stats = [
    {
      title: 'Total Blogs',
      value: loading ? <Skeleton width={60} /> : counts.blogs,
      icon: <Article />,
      color: '#F25828',
      gradient: 'linear-gradient(98.76deg, #F25828 -45.06%, #F25828 34.8%, #F7CE25 114.24%)',
      onClick: () => navigate('/admin/blogs'),
    },
    {
      title: 'News Articles',
      value: loading ? <Skeleton width={60} /> : counts.news,
      icon: <TrendingUp />,
      color: '#3C60B6',
      gradient: 'linear-gradient(98.76deg, #3C60B6 -45.06%, #3C60B6 34.8%, #4EB2CE 114.24%)',
      onClick: () => navigate('/admin/news'),
    },
    {
      title: 'New Enquiries',
      value: loading ? <Skeleton width={60} /> : counts.enquiries,
      icon: <Message />,
      color: '#5E3EA1',
      gradient: 'linear-gradient(98.76deg, #5E3EA1 -45.06%, #5E3EA1 34.8%, #8B359E 114.24%)',
      onClick: () => navigate('/admin/enquiries'),
    },
    {
      title: 'Total FAQs',
      value: loading ? <Skeleton width={60} /> : counts.faqs,
      icon: <People />,
      color: '#2E7D32',
      gradient: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%)',
      onClick: () => navigate('/admin/faqs'),
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A1A1A', mb: 1 }}>
          Welcome back, Admin!
        </Typography>
        <Typography variant="body1" sx={{ color: '#666' }}>
          Here's what's happening with Ronohub today.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        {stats.map((stat, index) => (
          <Box key={index} sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
            <StatCard {...stat} />
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(66.66% - 12px)' } }}>
          <Paper sx={{ p: 3, borderRadius: '16px', boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)', minHeight: 400 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
              Recent Activity
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {loading ? (
                Array.from(new Array(5)).map((_, index) => (
                  <Box key={index} sx={{ p: 2, borderRadius: '12px', bgcolor: '#F8F9FA', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Skeleton variant="text" width="60%" />
                      <Skeleton variant="text" width="40%" />
                    </Box>
                  </Box>
                ))
              ) : recentActivities.length > 0 ? (
                recentActivities.map((activity) => (
                  <Box
                    key={activity.id}
                    sx={{
                      p: 2,
                      borderRadius: '12px',
                      bgcolor: '#F8F9FA',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                      <Avatar sx={{ bgcolor: activity.type === 'News' ? '#3C60B6' : '#F25828', width: 40, height: 40 }}>
                        {activity.type === 'News' ? 'N' : 'B'}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {activity.title}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#999' }}>
                          {new Date(activity.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} • {activity.category}
                        </Typography>
                      </Box>
                    </Stack>
                    <Typography 
                      variant="body2" 
                      onClick={() => navigate(`/admin/${activity.type === 'News' ? 'news' : 'blogs'}`)}
                      sx={{ 
                        color: '#F25828', 
                        fontWeight: 600, 
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      View
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography sx={{ textAlign: 'center', py: 4, color: '#999' }}>
                  No recent activities found.
                </Typography>
              )}
            </Box>
          </Paper>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(33.33% - 12px)' } }}>
          <Paper sx={{ p: 3, borderRadius: '16px', boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)', minHeight: 400 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
              Quick Actions
            </Typography>
            <Stack spacing={2}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<Article />}
                onClick={() => navigate('/admin/blogs')}
                sx={{
                  justifyContent: 'flex-start',
                  py: 1.5,
                  borderRadius: '12px',
                  textTransform: 'none',
                  borderColor: '#E0E0E0',
                  color: '#1A1A1A',
                  '&:hover': { borderColor: '#F25828', bgcolor: 'rgba(242, 88, 40, 0.04)' },
                }}
              >
                Manage Blog Posts
              </Button>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<TrendingUp />}
                onClick={() => navigate('/admin/news')}
                sx={{
                  justifyContent: 'flex-start',
                  py: 1.5,
                  borderRadius: '12px',
                  textTransform: 'none',
                  borderColor: '#E0E0E0',
                  color: '#1A1A1A',
                  '&:hover': { borderColor: '#3C60B6', bgcolor: 'rgba(60, 96, 182, 0.04)' },
                }}
              >
                Manage News Updates
              </Button>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<Message />}
                onClick={() => navigate('/admin/enquiries')}
                sx={{
                  justifyContent: 'flex-start',
                  py: 1.5,
                  borderRadius: '12px',
                  textTransform: 'none',
                  borderColor: '#E0E0E0',
                  color: '#1A1A1A',
                  '&:hover': { borderColor: '#5E3EA1', bgcolor: 'rgba(94, 62, 161, 0.04)' },
                }}
              >
                Respond to Enquiries
              </Button>
            </Stack>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
